import * as React from 'react';
import { usePrevious } from '@resi-media/resi-ui';
import { produce } from 'immer';
import { useParams } from 'react-router';
import { createContainer } from 'react-tracked';
import { LAYOUT_CONTAINER_ID, ROWS_PER_PAGE } from '@sites/constants/layout';
import { useTileColumnCount } from '@sites/hooks/useTileColumnCount';
import { useFeed } from '../feedContext';

type PaginationContextState = {
  currentPage: number;
  max: number;
  min: number;
};

type PaginationContextHook = PaginationContextState & {
  isPlaylist?: boolean;
  setPageData: (page: number) => void;
  total: number;
};

enum PAGINATION_CONTEXT_ACTION_TYPES {
  SET_PAGE_DATA,
}

type PaginationActions = {
  payload: {
    currentPage: number;
    max: number;
    min: number;
  };
  type: PAGINATION_CONTEXT_ACTION_TYPES.SET_PAGE_DATA;
};

const initialState = (paginationCutoffLength: number, isPlaylist?: boolean) => {
  return {
    currentPage: 1,
    isPlaylist,
    max: paginationCutoffLength,
    min: 1,
  };
};

const paginationContextReducer = (state: PaginationContextState, action: PaginationActions) => {
  switch (action.type) {
    case PAGINATION_CONTEXT_ACTION_TYPES.SET_PAGE_DATA:
      state.currentPage = action.payload.currentPage;
      state.max = action.payload.max;
      state.min = action.payload.min;
      break;
    default:
      break;
  }
};

const curriedReducerFunction = produce(paginationContextReducer);

const useValues = ({ isPlaylist }: { isPlaylist?: boolean }): readonly [PaginationContextHook, () => void] => {
  const columns = useTileColumnCount();
  const previousColumns = usePrevious(columns);
  const { playlistId } = useParams<'playlistId'>();
  const { playlists, videosMap } = useFeed();
  const videos = videosMap.get(playlistId ?? '');
  const totalPlaylist = playlists?.length ?? 0;
  const totalVideos = videos?.length ?? 0;
  const total = isPlaylist ? totalPlaylist : totalVideos;

  const paginationCutoffLength = React.useMemo(
    () => (isPlaylist ? ROWS_PER_PAGE : columns * ROWS_PER_PAGE),
    [columns, isPlaylist]
  );
  const [{ currentPage, max, min }, dispatch] = React.useReducer(
    curriedReducerFunction,
    initialState(paginationCutoffLength, isPlaylist)
  );

  // Scroll to top on route changes (does not handle pagination)
  React.useEffect(() => {
    const container = document.getElementById(LAYOUT_CONTAINER_ID);
    if (container) {
      container.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [currentPage]);

  React.useEffect(() => {
    if (!isPlaylist && columns !== previousColumns && previousColumns !== undefined) {
      dispatch({
        type: PAGINATION_CONTEXT_ACTION_TYPES.SET_PAGE_DATA,
        payload: {
          currentPage: 1,
          max: paginationCutoffLength,
          min: 1,
        },
      });
    }
  }, [columns, isPlaylist, paginationCutoffLength, previousColumns]);

  const setPageData = React.useCallback(
    (page: number) => {
      const isNextPage = page > currentPage;
      const isLastPage = total / paginationCutoffLength < page;

      dispatch({
        type: PAGINATION_CONTEXT_ACTION_TYPES.SET_PAGE_DATA,
        payload: {
          currentPage: page,
          max: isLastPage ? total : page * paginationCutoffLength,
          min: isNextPage ? min + paginationCutoffLength : min - paginationCutoffLength,
        },
      });
    },
    [currentPage, min, paginationCutoffLength, total]
  );

  const pagination = React.useMemo(() => {
    return {
      currentPage,
      isPlaylist,
      max,
      min,
      total,
      setPageData,
    };
  }, [currentPage, isPlaylist, max, min, setPageData, total]);

  return [
    pagination,
    () => {
      throw new Error('use functions in the state');
    },
  ];
};

const {
  Provider: PaginationProvider,
  useTrackedState: usePagination,
  useUpdate: usePaginationUpdate,
} = createContainer<PaginationContextHook, () => void, { children: React.ReactNode; isPlaylist?: boolean }>(useValues);

export { PaginationProvider, usePagination, usePaginationUpdate };
export type { PaginationContextHook };
